import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import AuthDialog from "@/components/AuthDialog";
import {
  LayoutGrid,
  ImagePlus,
  Wand2,
  Frame,
  MapPin,
  Share2,
  ArrowRight,
  Check,
} from "lucide-react";

const FEATURES = [
  {
    icon: LayoutGrid,
    title: "Project galleries",
    body: "Organise every space visually — rooms, references and decisions live together, never buried in folders.",
  },
  {
    icon: ImagePlus,
    title: "Unified media library",
    body: "Pull images from your camera roll, Pinterest or any URL. Photos, PDFs and CAD drawings, all in one place.",
  },
  {
    icon: Wand2,
    title: "AI tagging & palettes",
    body: "Every upload is auto-categorised with room, style, materials and a colour palette — searchable in seconds.",
  },
  {
    icon: Frame,
    title: "Moodboard studio",
    body: "Drag, layer and rotate. Or let AI compose a board from a mood and your selected references in one click.",
  },
  {
    icon: MapPin,
    title: "Floorplan markups",
    body: "Drop pins on plans, attach references and notes so the whole team reads the space the same way.",
  },
  {
    icon: Share2,
    title: "Share with clients",
    body: "Send a polished, read-only link or export to PDF. Invite collaborators with the right level of access.",
  },
];

const STEPS = [
  { n: "01", t: "Collect", d: "Drop in everything — site photos, product links, materials, inspiration." },
  { n: "02", t: "Compose", d: "Build moodboards, tag references and mark up floorplans by room." },
  { n: "03", t: "Present", d: "Share a beautiful client view or export the whole project to PDF." },
];

export default function Landing() {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  // Signed-in users skip the marketing page
  if (user && typeof user === "object") {
    return <Navigate to="/studio" replace />;
  }

  function openAuth(mode) {
    setAuthMode(mode);
    setAuthOpen(true);
  }

  return (
    <div className="min-h-screen bg-white text-stone-900" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#f7f5f0]/80 backdrop-blur-md border-b border-stone-200/70">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="font-display text-xl tracking-tight flex items-center gap-2" data-testid="landing-brand">
            <span className="w-2 h-2 rounded-full bg-[#C27A62]" />
            Atelier Studio
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => openAuth("login")}
              data-testid="landing-signin-button"
              className="rounded-full text-stone-700 hover:bg-stone-200/60"
            >
              Sign in
            </Button>
            <Button
              onClick={() => openAuth("register")}
              data-testid="landing-start-trial-nav"
              className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
            >
              Start free trial
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "#f7f5f0" }}>
        <div className="absolute inset-0 dotted-canvas opacity-40 pointer-events-none" />
        <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#C27A62] font-semibold mb-5">
              For interior architects & design studios
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-stone-900">
              Every reference, room and material —
              <span className="text-stone-500"> in one quiet place.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-stone-600 max-w-xl leading-relaxed">
              Atelier Studio is the visual workspace built for the way studios actually work.
              Collect inspiration, build moodboards, mark up floorplans and present to
              clients — without the chaos of scattered folders and chat threads.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                onClick={() => openAuth("register")}
                data-testid="landing-start-trial-button"
                className="rounded-full h-12 px-7 bg-stone-900 text-white hover:bg-stone-800 text-sm"
              >
                Start your 28-day free trial
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
              <Button
                variant="outline"
                onClick={() => openAuth("login")}
                data-testid="landing-hero-signin"
                className="rounded-full h-12 px-7 border-stone-300 text-stone-800 hover:bg-white text-sm"
              >
                Sign in
              </Button>
            </div>
            <p className="mt-4 text-xs text-stone-500 flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#8A9A86]" strokeWidth={2} /> No card required
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[#8A9A86]" strokeWidth={2} /> Cancel anytime
              </span>
            </p>
          </motion.div>

          {/* Product collage (CSS-built, on-brand) */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
            className="relative h-[420px] sm:h-[460px] hidden md:block"
          >
            {/* Moodboard tile */}
            <div className="absolute left-0 top-4 w-60 h-72 rounded-2xl bg-white shadow-xl border border-stone-200 p-4 -rotate-3">
              <div className="h-32 rounded-xl bg-[#8A9A86]/80 mb-3 flex items-end p-3">
                <span className="text-white/90 font-display text-lg">Coastal calm</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {["#E6EEF2", "#C9D6CF", "#B6A38C", "#E8E0D2", "#22404e", "#C27A62"].map((c) => (
                  <div key={c} className="aspect-square rounded-md" style={{ background: c }} />
                ))}
              </div>
              <p className="mt-3 text-[10px] uppercase tracking-[0.15em] text-stone-400 font-semibold">
                Palette · auto-generated
              </p>
            </div>

            {/* Materials card */}
            <div className="absolute right-2 top-0 w-56 rounded-2xl bg-white shadow-xl border border-stone-200 p-5 rotate-2">
              <p className="text-[10px] uppercase tracking-[0.15em] text-stone-400 font-semibold mb-3">
                Materials
              </p>
              {[
                ["Oak", "#B6A38C"],
                ["Travertine", "#E8E0D2"],
                ["Linen", "#D8CAB0"],
                ["Brass", "#C9A227"],
              ].map(([name, c]) => (
                <div key={name} className="flex items-center gap-3 py-1.5">
                  <span className="w-4 h-4 rounded-full" style={{ background: c }} />
                  <span className="text-sm text-stone-700">{name}</span>
                </div>
              ))}
            </div>

            {/* Floorplan card */}
            <div className="absolute right-6 bottom-0 w-64 h-44 rounded-2xl bg-white shadow-xl border border-stone-200 p-4 -rotate-2">
              <p className="text-[10px] uppercase tracking-[0.15em] text-stone-400 font-semibold mb-2">
                Floorplan · 2 pins
              </p>
              <div className="relative h-28 rounded-lg bg-stone-50 border border-stone-200 overflow-hidden">
                <svg viewBox="0 0 200 110" className="w-full h-full">
                  <rect x="8" y="8" width="184" height="94" fill="none" stroke="#d6d3d1" strokeWidth="2" />
                  <line x1="100" y1="8" x2="100" y2="102" stroke="#d6d3d1" strokeWidth="2" />
                  <line x1="100" y1="55" x2="192" y2="55" stroke="#d6d3d1" strokeWidth="2" />
                </svg>
                <span className="absolute left-[26%] top-[34%] w-5 h-5 rounded-full bg-[#C27A62] border-2 border-white shadow flex items-center justify-center text-[10px] text-white">1</span>
                <span className="absolute left-[70%] top-[60%] w-5 h-5 rounded-full bg-[#8A9A86] border-2 border-white shadow flex items-center justify-center text-[10px] text-white">2</span>
              </div>
            </div>

            {/* Tag chip */}
            <div className="absolute left-16 bottom-10 bg-stone-900 text-white text-xs rounded-full px-4 py-2 shadow-lg rotate-1 flex items-center gap-2">
              <Wand2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              AI tagged: bedroom · japandi
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-14"
        >
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#C27A62] font-semibold mb-3">
            Everything in one canvas
          </p>
          <h2 className="font-display text-3xl md:text-4xl text-stone-900 leading-tight">
            Built for the way studios actually work
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
              data-testid={`landing-feature-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-stone-100 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-stone-800" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl text-stone-900 mb-2">{f.title}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Workflow */}
      <section style={{ background: "#f7f5f0" }} className="border-y border-stone-200/70">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <h2 className="font-display text-3xl md:text-4xl text-stone-900 mb-14 max-w-xl leading-tight">
            From first reference to final presentation
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="border-t-2 border-stone-900 pt-5"
              >
                <span className="font-display text-2xl text-[#C27A62]">{s.n}</span>
                <h3 className="font-display text-2xl text-stone-900 mt-2 mb-2">{s.t}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-[#1c1b17] text-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-display text-4xl md:text-5xl leading-tight max-w-3xl mx-auto">
              Give your studio a calmer, more beautiful workflow.
            </h2>
            <p className="mt-5 text-stone-300 max-w-xl mx-auto">
              Try Atelier Studio free for 28 days. Bring your next project to life in one visual workspace.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <Button
                onClick={() => openAuth("register")}
                data-testid="landing-cta-start-trial"
                className="rounded-full h-12 px-8 bg-white text-stone-900 hover:bg-stone-100 text-sm"
              >
                Start your free trial
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
              <Button
                variant="ghost"
                onClick={() => openAuth("login")}
                data-testid="landing-cta-signin"
                className="rounded-full h-12 px-8 text-white hover:bg-white/10 text-sm"
              >
                Sign in
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1c1b17] border-t border-white/10 text-stone-400">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <div className="font-display text-lg text-white flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#C27A62]" />
            Atelier Studio
          </div>
          <p className="text-xs">© {new Date().getFullYear()} Atelier Studio — visual studio for interior architects.</p>
        </div>
      </footer>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
    </div>
  );
}
